import { ELEMENT_LAYOUT, GRADE_DISPLAY_BOX } from "./layout/constants";

/**
 * Corpo do nome da nota por nota geral, herdado do handler.py da Lambda —
 * é ajuste tipográfico deliberado (cada nome tem largura diferente).
 *
 * Já as âncoras horizontais irregulares do legado (236 / 237.2 / 237.3 /
 * 237.4 / 237.5 / 238 / 239.6) e os baselines 747.5 / 747.7 / 748 não são
 * reproduzidos: eram compensação manual de um bug de métrica. O legado
 * centralizava com `stringWidth` em Montserrat-Light 27 enquanto desenhava em
 * Bold 27, e media o nome da nota sempre em corpo 5.0 mesmo desenhando em
 * 4.5 / 4.8 / 5.8. Medindo e desenhando com a mesma métrica, um único eixo
 * (centro do quadro da nota) e um único baseline bastam.
 */
const GRADE_LABEL_FONT_SIZE = {
  1: 5.8,
  2: 5.5,
  3: 5.0,
  4: 4.5,
  "4.5": 4.8,
  5: 4.5,
  "5.5": 4.5,
  6: 5.0,
  "6.5": 4.5,
  7: 4.5,
  "7.5": 5.0,
  9: 5.0,
};

const gradeCanonicalKey = (grade) => {
  if (grade === null || grade === undefined) return "";
  const raw = String(grade).trim();
  if (!raw) return raw;
  if (raw.toUpperCase() === "EP") return "EP";
  if (raw === "N/P" || raw.replace(/\s/g, "").toUpperCase() === "NP") return "N/P";
  const normalized = raw.replace(",", ".");
  const value = Number(normalized);
  if (Number.isNaN(value)) return raw;
  if (Math.abs(value - Math.round(value * 2) / 2) < 1e-9) {
    if (value === Math.trunc(value)) return String(Math.trunc(value));
    return `${value.toFixed(1)}`.replace(/\.0$/, "");
  }
  return raw;
};

const gradeLabelFontSize = (grade) => {
  const key = gradeCanonicalKey(grade);
  return GRADE_LABEL_FONT_SIZE[key] ?? ELEMENT_LAYOUT.nome_nota.font[1];
};

/** Centro horizontal do quadro dourado da nota (eixo geométrico). */
const gradeDisplayCenterX = (slot) =>
  GRADE_DISPLAY_BOX.x + GRADE_DISPLAY_BOX.width / 2 + (slot?.x_offset || 0);

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const compactGradeName = (card) => {
  if (!isBlank(card.nome_nota)) {
    const provided = String(card.nome_nota).trim().toUpperCase();
    const aliases = {
      PRISTINE: "P",
      "GEM MINT": "GM",
      MINT: "M",
      "NEAR MINT +": "NM+",
      "NEAR MINT": "NM",
      "SLIGHTLY PLAYED +": "SP+",
      "SLIGHTLY PLAYED": "SP",
      "SLIGHTLY PLAYED -": "SP-",
      "MODERATELY PLAYED +": "MP+",
      "MODERATELY PLAYED": "MP",
      "MODERATELY PLAYED -": "MP-",
      "HEAVILY PLAYED +": "HP+",
      "HEAVILY PLAYED": "HP",
      "HEAVILY PLAYED -": "HP-",
      DAMAGED: "D",
      "POOR +": "PR+",
      POOR: "PR",
      "EVENT PROMO": "EP",
      "NOT GRADED": "NG",
    };
    return aliases[provided] || provided;
  }
  const fallback = {
    10: "P",
    9.5: "GM",
    9: "M",
    8.5: "NM+",
    8: "NM",
    7.5: "SP+",
    7: "SP",
    6.5: "SP-",
    6: "SP-",
    5.5: "MP+",
    5: "MP+",
    4.5: "MP",
    4: "MP-",
    3.5: "HP+",
    3: "HP",
    2.5: "HP-",
    2: "D",
    1.5: "PR+",
    1: "PR",
    EP: "EP",
    "N/P": "NG",
  };
  return fallback[String(card.notaGeral).trim()] || "";
};

const gradeLineText = (card) => {
  const grade = isBlank(card.notaGeral) ? "" : String(card.notaGeral).trim();
  const sigla = compactGradeName(card);
  if (grade && sigla) return `${grade} ${sigla}`;
  return grade;
};

/**
 * Texto da linha de autógrafo na etiqueta (paridade com mapAutographScoreForLambda
 * + normalização da Lambda: Autentico → AUTH. AUTO; 8/9/10 → AUTO N).
 * Distingue autógrafo auténtico do AUTHENTIC da avaliação sem nota.
 */
const autographLineText = (notaAutografo) => {
  if (isBlank(notaAutografo)) return "";
  const raw = String(notaAutografo).trim();
  const lower = raw.toLowerCase().replace(/\s+/g, " ");

  if (
    lower === "autentico" ||
    lower === "authentic" ||
    lower === "auth. auto" ||
    lower === "auth auto" ||
    lower === "auth.auto" ||
    lower === "auto authentic" ||
    lower === "auto autentico"
  ) {
    return "AUTH. AUTO";
  }

  const autoNumeric = lower.match(/^auto\s*(8|9|10)$/);
  if (autoNumeric) return `AUTO ${autoNumeric[1]}`;

  if (raw === "8" || raw === "9" || raw === "10") {
    return `AUTO ${raw}`;
  }

  return raw;
};

export {
  gradeCanonicalKey,
  gradeLabelFontSize,
  gradeDisplayCenterX,
  compactGradeName,
  gradeLineText,
  autographLineText
};
